/* =============================================================================
 *  GraphiteClock — a small, self-contained analogue clock (inline SVG, no deps).
 *  Ticks once a second on its own isolated state, so it never re-renders the wall.
 *  Colours come from the graphite tokens (face/ticks/hands), second hand accent.
 * ============================================================================= */

import React, { useEffect, useState } from 'react';

const polar = (deg: number, len: number, c = 50): { x: number; y: number } => {
  const rad = (deg - 90) * (Math.PI / 180);
  return { x: c + len * Math.cos(rad), y: c + len * Math.sin(rad) };
};

const GraphiteClock: React.FC<{ size?: number }> = ({ size = 46 }) => {
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const s = now.getSeconds();
  const m = now.getMinutes();
  const h = now.getHours() % 12;
  const hour = polar(h * 30 + m * 0.5, 24);
  const min = polar(m * 6 + s * 0.1, 33);
  const sec = polar(s * 6, 37);

  return (
    <svg
      className="gclock"
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="img"
      aria-label={`Current time ${now.toLocaleTimeString()}`}
    >
      <title>{now.toLocaleTimeString()}</title>
      <circle className="gclock__face" cx="50" cy="50" r="46" />
      {Array.from({ length: 12 }).map((_, i) => {
        const major = i % 3 === 0;
        const a = polar(i * 30, 46);
        const b = polar(i * 30, major ? 39 : 42);
        return (
          <line
            key={i}
            className={`gclock__tick${major ? ' gclock__tick--major' : ''}`}
            x1={a.x} y1={a.y} x2={b.x} y2={b.y}
          />
        );
      })}
      <line className="gclock__hand gclock__hand--hour" x1="50" y1="50" x2={hour.x} y2={hour.y} />
      <line className="gclock__hand gclock__hand--min" x1="50" y1="50" x2={min.x} y2={min.y} />
      <line className="gclock__hand gclock__hand--sec" x1="50" y1="50" x2={sec.x} y2={sec.y} />
      <circle className="gclock__pin" cx="50" cy="50" r="2.6" />
    </svg>
  );
};

export default GraphiteClock;
